import React from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  FormControlLabel
} from "@mui/material";
import { EnhancedTableToolbar } from "./EnhancedTableToolbar";
import { DesktopDatePicker, LocalizationProvider } from "@mui/lab";
import DateAdapter from "@mui/lab/AdapterDateFns";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { withStyles } from "@mui/styles";
import { tooltipClasses } from "@mui/material/Tooltip";
import spinner from "../assign-prescriptions/images/spinner.gif";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import { withTranslation } from "react-i18next";
import enLocale from "date-fns/locale/en-US";
import frLocale from "date-fns/locale/fr";
import Selector from '../select';
import SearchableSelect from "../common/SearchableSelect";
import HtmlEditor from "../feature-catalog-admin/HtmlEditor";
import Switch from '@mui/material/Switch';
import ILocalAGGrid, {
  getDefaultColDef,
  createServerSideDatasource,
  foldFiltersToSearchText,
  getActionsColumn,
  actionMenuItem,
  openAgGridContextMenu,
} from '../grid/iLocalAGGrid';
import { QueueLaneTiles } from "../workflowQueue/QueueLaneTiles";
import {
  applyLaneToFilterBody,
  normalizeLane,
  statusListForLane,
  taskManagerHref,
} from "../workflowQueue/queueModel";
import { invalidateQueueCache, loadQueueSummary as fetchQueueSummary } from "../workflowQueue/queueApi";
import { navigate } from "@reach/router";
const TaskTypeEnum = require('./taskTypeEnum').default;

const { log } = console;
const PICKUP_TASK_SYNC_KEY = 'pickupChangeTasksUpdatedAt';

const getTaskDetailTimezone = (timezone) => {
  if ([undefined, null, "", "null", "undefined"].includes(timezone)) {
    return moment.tz.guess();
  }

  const value = String(timezone).trim();
  if (moment.tz.zone(value)) {
    return value;
  }

  const ianaMatch = value.match(/[A-Za-z_]+\/[A-Za-z_]+(?:\/[A-Za-z_]+)?/);
  if (ianaMatch && moment.tz.zone(ianaMatch[0])) {
    return ianaMatch[0];
  }

  const lastToken = value.substring(value.lastIndexOf(" ") + 1);
  if (moment.tz.zone(lastToken)) {
    return lastToken;
  }

  return moment.tz.guess();
};

const BootstrapDialogTitle = (props) => {
  const { children, onDialogClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, px: 3, py: 2, fontWeight: 700, color: "#0f172a" }} {...other}>
      {children}
      {onDialogClose && (
        <IconButton
          aria-label="close"
          onClick={onDialogClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
    </DialogTitle>
  );
};

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onDialogClose: PropTypes.func.isRequired,
};

const HTMLTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: "#f5f5f9",
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: "0.8125rem",
    border: "1px solid #dadde9",
    borderRadius: "5px",
  },
}));

const useStyles = (theme) => ({
  primaryButton: {
    background: "#6cca98",
    color: "#fff",
    "&:hover": {
      background: "#6cca98",
      color: "#fff",
    },
    "&:disabled": {
      color: "rgba(0, 0, 0, 0.38)",
      cursor: "not-allowed",
      background: "#E4E5E8",
    },
  },
  deleteButton: {
    "&:hover": {
      background: "#6cca98",
      color: "#fff",
    },
    "&:disabled": {
      color: "rgba(0, 0, 0, 0.38)",
      cursor: "not-allowed",
      background: "#E4E5E8",
    },
  },
  spinner: {
    position: "absolute",
    background: "rgba(255,255,255,.4)",
    width: "100%",
    height: "100%",
    zIndex: "9999",
    top: 0,
    left: 0,
  },
  spinnerImage: {
    top: "calc(50% - 30.5px)",
    left: "calc(50% - 65px)",
    position: "absolute",
    width: "130px",
    userSelect: "none",
  },
});

let filterObj = {
  searchtext: "",
  pageNo: 0,
  pageSize: 10,
  sortOrder: "desc",
  sortColumn: "created_on",
  tableName: "task_manager",
  statusList: [],
  priorityList: [],
  actionList: [],
  taskTypeList: [],
  searchObject: null,
};

const TaskSortObj = {
  task_name: { tableName: "task_manager", sortColumn: "task_name" },
  taskType: { tableName: "task_manager", sortColumn: "task_type_id" },
  status: { tableName: "task_manager", sortColumn: "status" },
  priority: { tableName: "task_manager", sortColumn: "priority" },
  reminder_date: { tableName: "task_manager", sortColumn: "reminder_date" },
  due_date: { tableName: "task_manager", sortColumn: "due_date" },
  patientName: { tableName: "customers", sortColumn: "name" },
  userName: { tableName: "users", sortColumn: "name" },
  selected_emails: { tableName: "task_manager", sortColumn: "selected_emails" },
  is_enabled: { tableName: "task_manager", sortColumn: "is_enabled" },
};

const PRIORITY = ["Low", "Medium", "High", "Critical"];
const STATUS = ["Open", "InProgress", "Complete", "Cancelled"];
const TASK_COL_LEFT = { pr: 1.5, boxSizing: "border-box", minWidth: 0 };
const TASK_COL_RIGHT = { pl: 1.5, boxSizing: "border-box", minWidth: 0 };
const TASK_DATE_FIELD_SX = {
  width: "100%",
  "& .MuiInput-root": {
    overflow: "visible",
    position: "relative",
    paddingRight: "36px",
  },
  "& .MuiInputAdornment-root": {
    position: "absolute",
    right: 0,
    margin: 0,
  },
  "& .MuiInput-input": {
    color: "#0f172a",
    fontWeight: 500,
    minWidth: 0,
  },
  "& .MuiIconButton-root": {
    padding: "4px",
  },
};
const statusDisplay = (value) =>
  value === "InProgress" ? "In Progress" : value || "Open";

// const ACTION = ["Open", "Complete", "Delete", "Close"];

class TaskManager extends React.Component {
  static getTaskIdFromLocation = () => {
    if (typeof window === "undefined" || !window.location) {
      return "";
    }
    return new URLSearchParams(window.location.search).get("taskId") || "";
  };

  static getLaneFromLocation = () => {
    if (typeof window === "undefined" || !window.location) {
      return "";
    }
    const params = new URLSearchParams(window.location.search);
    const lane = params.get("lane") || "";
    if (lane) return normalizeLane(lane);
    if (params.get("type") === "delivery_change") return "delivery_change";
    return "";
  };

  constructor(props) {
    super(props);
    const routeTaskId = TaskManager.getTaskIdFromLocation();
    const routeLane = TaskManager.getLaneFromLocation();
    this.state = {
      openTaskManagerDialog: false,
      taskTitle: "",
      action: "Open",
      reminderDate: new Date(),
      dueDate: new Date(),
      userDataList: [],
      selectedUserID: "",
      selectedUserName: "",
      taskTypeList: [],
      selectedTaskTypeID: "",
      selectedTaskName: "",
      status: "Open",
      priority: "Low",
      patientDataList: [],
      selectedPatientID: "",
      selectedPatientName: "",
      loadingSpinner: false,
      operation: "Add",
      taskDetails: "",
      orderCode: "",
      totalRecords: 0,
      data: [],
      isDataEmpty: false,
      selectedData: [],
      sortID: null,
      anchorEl: null,
      selectedRowID: null,
      openDeleteDialog: false,
      confirmedDeleteList: [],
      filterStatusList: [],
      filterActionList: [],
      filterPriorityList: [],
      showPickupChangeOnly: routeLane === "delivery_change",
      activeLane: routeLane,
      queueSummary: {},
      queueSummaryLoading: true,
      routeTaskId,
      openUserAutoComplete: false,
      openPatientAutoComplete: false,
      isTaskTitleRequired: false,
      isTaskDetailsRequired: false,
      isUserRequired: false,
      isPatientRequired: false,
      selectedUser: "",
      selectedUserInputValue: "",
      selectedPatient: "",
      selectedPatientInputValue: "",
      locale: localStorage.getItem('i18nextLng') === 'en' ? enLocale : frLocale,
      currentSelection: null,
      initialValue: [],
      accountValues: [],
      parent_account_id: null,
      selectedEmails: null,
      isEanbled: true,
      isTaskEmailRequired: false
    };
    applyLaneToFilterBody(filterObj, routeLane, []);
    filterObj.__lane = routeLane || "";
    filterObj.pageNo = 0;
    filterObj.statusList = routeLane ? statusListForLane(routeLane) : [];
  }

  componentDidMount = async () => {
    this.callInitialAPIs();
    this.loadQueueSummary();
    if (this.state.routeTaskId) {
      this.openTaskFromRoute(this.state.routeTaskId);
    }
  };

  loadQueueSummary = () => {
    const self = this;
    fetchQueueSummary()
      .then(function (result) {
        self.setState({ queueSummary: result.summary, queueSummaryLoading: false });
      })
      .catch(function () {
        self.setState({ queueSummaryLoading: false });
      });
  };

  applyLaneFilterBody = (lane) => {
    const normalized = normalizeLane(lane);
    applyLaneToFilterBody(filterObj, normalized, this.state.taskTypeList);
    filterObj.__lane = normalized || "";
    filterObj.pageNo = 0;
    if (normalized) {
      filterObj.statusList = statusListForLane(normalized);
    } else {
      filterObj.statusList = [];
    }
  };

  handleLaneSelect = (lane) => {
    const nextLane = this.state.activeLane === lane ? "" : lane;
    this.applyLaneFilterBody(nextLane);
    this.setState(
      {
        activeLane: nextLane,
        showPickupChangeOnly: nextLane === "delivery_change",
        selectedData: [],
      },
      () => {
        this.refreshGrid();
        navigate(taskManagerHref("", "", nextLane), { replace: true });
      }
    );
  };

  applyLaneFromLocation = () => {
    const routeLane = TaskManager.getLaneFromLocation();
    if (routeLane === (this.state.activeLane || "")) {
      return;
    }
    this.applyLaneFilterBody(routeLane);
    this.setState(
      {
        activeLane: routeLane,
        showPickupChangeOnly: routeLane === "delivery_change",
        selectedData: [],
      },
      () => {
        this.refreshGrid();
      }
    );
  };

  componentDidUpdate(prevProps) {
    const prevSearch =
      prevProps && prevProps.location ? prevProps.location.search : "";
    const nextSearch = this.props.location ? this.props.location.search : "";
    if (prevSearch !== nextSearch) {
      this.applyLaneFromLocation();
    }
  }

  emitNotificationEvent = (UserID) => {
    window &&
      window.notificationSocket.emit("UserSendingNotificationEvent", {
        UserID,
        Message: "User sending notification events from connect!!",
      });
  };

  ensureSelectItemInList = (list, id, name) => {
    const items = (list || []).slice();
    if (!id) {
      return items;
    }
    const sid = String(id);
    const exists = items.some((item) => String(item.id) === sid);
    if (!exists) {
      items.unshift({ id: sid, name: name || sid });
    }
    return items;
  };

  gettingUserLists = (queryParam, selectedItem) => {
    try {
      const searchText = queryParam == null ? "" : queryParam;
      fetch(
        process.env.REACT_APP_BASEURL +
        "taskmanager/UsersList?searchText=" +
        encodeURIComponent(searchText),
        {
          method: "GET",
          withCredentials: true,
          headers: {
            Authorization:
              "Bearer " +
              JSON.parse(sessionStorage.getItem("user")).signInUserSession
                .idToken.jwtToken,
            "Content-Type": "application/json",
          },
        }
      )
        .then((response) => response.json())
        .then((response) => {
          const results =
            response && Array.isArray(response.results) ? response.results : [];
          const mapped = results.map((user) => ({
            id: String(user.id),
            name: user.name || "",
          }));
          const selectedId = selectedItem
            ? selectedItem.id
            : this.state.selectedUserID;
          const selectedName = selectedItem
            ? selectedItem.name
            : this.state.selectedUserName;
          this.setState({
            userDataList: this.ensureSelectItemInList(
              mapped,
              selectedId,
              selectedName
            ),
          });
        });
    } catch (ErrorException) {
      log(
        "(TaskManager): Error ocurred while getting the users details!",
        ErrorException
      );
    }
  };

  gettingTaskManagerTypesList = () => {
    try {
      fetch(process.env.REACT_APP_BASEURL + "taskmanager/TaskType", {
        method: "GET",
        withCredentials: true,
        headers: {
          Authorization:
            "Bearer " +
            JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
              .jwtToken,
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then((response) => {
          if (
            response &&
            Array.isArray(response["results"]) &&
            response["results"].length > 0
          ) {
            const defaultType =
              response["results"].find(
                (item) =>
                  item &&
                  item.id !== TaskTypeEnum.PickupChangeRequest &&
                  item.id !== TaskTypeEnum.RefillRequest
              ) || response["results"][0];
            this.setState({
              taskTypeList: response["results"],
              selectedTaskTypeID: defaultType.id,
              selectedTaskName: defaultType.name,
            }, () => {
              if (this.state.activeLane) {
                this.applyLaneFilterBody(this.state.activeLane);
                this.refreshGrid();
              }
            });
          } else this.setState({ taskTypeList: [], selectedTaskTypeID: "" });
        });
    } catch (ErrorException) {
      log(
        "(TaskManager): Error ocurred while getting the task types details!",
        ErrorException
      );
    }
  };

  gettingPatientList = (queryParam, selectedItem) => {
    try {
      const searchText = queryParam == null ? "" : queryParam;
      fetch(
        process.env.REACT_APP_BASEURL +
        "taskmanager/PatientList?searchText=" +
        encodeURIComponent(searchText),
        {
          method: "GET",
          withCredentials: true,
          headers: {
            Authorization:
              "Bearer " +
              JSON.parse(sessionStorage.getItem("user")).signInUserSession
                .idToken.jwtToken,
            "Content-Type": "application/json",
          },
        }
      )
        .then((response) => response.json())
        .then((response) => {
          const results =
            response && Array.isArray(response.results) ? response.results : [];
          const mapped = results.map((patient) => ({
            id: String(patient.id),
            name: patient.name || "",
          }));
          const selectedId = selectedItem
            ? selectedItem.id
            : this.state.selectedPatientID;
          const selectedName = selectedItem
            ? selectedItem.name
            : this.state.selectedPatientName;
          this.setState({
            patientDataList: this.ensureSelectItemInList(
              mapped,
              selectedId,
              selectedName
            ),
          });
        });
    } catch (ErrorException) {
      log(
        "(TaskManager): Error ocurred while getting the patients details!",
        ErrorException
      );
    }
  };

  loadAssigneeSelectLists = (selectedUser, selectedPatient) => {
    this.gettingUserLists("", selectedUser);
    this.gettingPatientList("", selectedPatient);
  };

  gettingTaskManagerDetailsFromDB = async () => {
    if (this.gridApi) {
      this.refreshGrid();
      return;
    }
  };

  openTaskFromRoute = async (taskId) => {
    try {
      this.setState({ loadingSpinner: true });
      const response = await fetch(process.env.REACT_APP_BASEURL + "taskmanager/details", {
        method: "POST",
        withCredentials: true,
        headers: {
          Authorization:
            "Bearer " +
            JSON.parse(sessionStorage.getItem("user")).signInUserSession
              .idToken.jwtToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          pageNo: 0,
          pageSize: 1,
          sortOrder: "desc",
          sortColumn: "created_on",
          tableName: "task_manager",
          statusList: [],
          priorityList: [],
          actionList: [],
          taskTypeList: [],
          searchtext: "",
          taskId,
        }),
      });
      const payload = await response.json();
      const nested = payload && payload.results;
      const row =
        nested && Array.isArray(nested.results) && nested.results.length > 0
          ? nested.results[0]
          : null;

      if (!row) {
        toast.warn("Task could not be found.");
        this.setState({ loadingSpinner: false });
        return;
      }

      this.setState(
        {
          data: [row],
          totalRecords: 1,
          loadingSpinner: false,
        },
        () => this.openPickupChangeTaskDialog(row, "Edit")
      );
    } catch (ErrorException) {
      log("(TaskManager): Error occurred while opening task from dashboard!", ErrorException);
      toast.error("Unable to open the selected task.");
      this.setState({ loadingSpinner: false });
    }
  };

  refreshGrid = () => {
    if (!this.gridApi) return;
    if (typeof this.gridApi.paginationGoToFirstPage === "function") {
      this.gridApi.paginationGoToFirstPage();
    }
    if (typeof this.gridApi.refreshServerSideStore === "function") {
      this.gridApi.refreshServerSideStore({ purge: true });
    } else if (typeof this.gridApi.refreshServerSide === "function") {
      this.gridApi.refreshServerSide({ purge: true });
    }
  };

  onGridReady = (params) => {
    this.gridApi = params.api;
    const datasource = createServerSideDatasource({
      filterBody: filterObj,
      applyFilterModel: (body, filterModel) => {
        const model = filterModel || {};

        // Status / Priority set-filters → API list filters
        const statusValues =
          model.status && Array.isArray(model.status.values)
            ? model.status.values.filter(Boolean)
            : [];
        const priorityValues =
          model.priority && Array.isArray(model.priority.values)
            ? model.priority.values.filter(Boolean)
            : [];

        body.statusList = statusValues;
        body.priorityList = priorityValues;
        body.actionList = [];
        applyLaneToFilterBody(body, this.state.activeLane, this.state.taskTypeList);
        body.__lane = this.state.activeLane || "";
        if (this.state.activeLane) {
          if (!statusValues.length) {
            body.statusList = statusListForLane(this.state.activeLane);
          }
        } else if (this.state.showPickupChangeOnly) {
          body.taskTypeList = [TaskTypeEnum.PickupChangeRequest];
        }
        delete body.taskId;

        // Enabled set-filter → searchtext Yes/No (API ilike on text fields is weak;
        // map to exact boolean via dedicated body flag if needed later)
        const enabledValues =
          model.is_enabled && Array.isArray(model.is_enabled.values)
            ? model.is_enabled.values.filter(Boolean)
            : [];
        if (enabledValues.length === 1) {
          body.is_enabled =
            enabledValues[0] === "Yes" || enabledValues[0] === true;
        } else {
          delete body.is_enabled;
        }

        body.searchtext = foldFiltersToSearchText(model, {
          excludeColIds: ["status", "priority", "actions", "is_enabled"],
        });
        body.searchObject =
          Object.keys(model).length > 0 ? model : null;
      },
      sortMap: TaskSortObj,
      pageZeroBased: true,
      getPageSize: () => filterObj.pageSize || 10,
      fetchPage: (body) =>
        fetch(process.env.REACT_APP_BASEURL + "taskmanager/details", {
          method: "POST",
          withCredentials: true,
          headers: {
            Authorization:
              "Bearer " +
              JSON.parse(sessionStorage.getItem("user")).signInUserSession
                .idToken.jwtToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            // API: { results: { results: [...], total: N } }
            const nested = response && response.results;
            const rows =
              nested && Array.isArray(nested.results) ? nested.results : [];
            return {
              results: rows,
              total: (nested && nested.total) || 0,
            };
          }),
      onSuccess: (results, total) => {
        this.setState({
          data: results || [],
          totalRecords: total || 0,
          loadingSpinner: false,
          isDataEmpty: !results || results.length === 0,
          selectedData: [],
        });
      },
      onError: () => this.setState({ loadingSpinner: false }),
    });
    if (typeof params.api.setServerSideDatasource === "function") {
      params.api.setServerSideDatasource(datasource);
    }
  };

  onRowSelected = (params) => {
    if (!params || !params.api) return;
    const selectedNodes = params.api.getSelectedNodes() || [];
    this.setState({
      selectedData: selectedNodes
        .map((n) => n.data && n.data.id)
        .filter(Boolean),
    });
  };

  onPaginationChanged = (params) => {
    if (!params.api) return;
    const newPageSize = params.api.paginationGetPageSize();
    if (newPageSize && filterObj.pageSize !== newPageSize) {
      filterObj.pageSize = newPageSize;
    }
  };

  openRowActionsMenu = (event, params, menuHelpers) => {
    const row = params && params.data;
    if (row && row.id != null) {
      // Intentional: avoid re-render before opening the actions menu.
      // eslint-disable-next-line react/no-direct-mutation-state
      this.state.selectedRowID = row.id;
      // eslint-disable-next-line react/no-direct-mutation-state
      this.state._contextRow = row;
    }
    const items = this.getContextMenuItems({
      ...params,
      column: params.column,
      node: params.node,
      value: params.value,
    });
    if (menuHelpers && typeof menuHelpers.openMenu === "function") {
      menuHelpers.openMenu(items);
      return;
    }
    openAgGridContextMenu(
      params,
      (menuHelpers && menuHelpers.positionEvent) || event
    );
  };

  getContextMenuItems = (params) => {
    const colId =
      params &&
      params.column &&
      (params.column.getColId
        ? params.column.getColId()
        : params.column.colId);
    if (colId !== "actions") return [];
    if (!(params.node && params.node.data)) return [];
    const { t } = this.props;
    const row = params.node.data;
    const isPickupChangeRequest = this.isPickupChangeRow(row);
    return [
      actionMenuItem({
        name: t("Task.ViewAction"),
        icon: "view",
        action: () => isPickupChangeRequest
          ? this.openPickupChangeTaskDialog(row, "View")
          : this.handleOpenView(),
      }),
      actionMenuItem({
        name: t("Task.EditAction"),
        icon: "edit",
        action: () => isPickupChangeRequest
          ? this.openPickupChangeTaskDialog(row, "Edit")
          : this.handleOpenEdit(),
      }),
      actionMenuItem({
        name: t("Task.DeleteAction"),
        icon: "delete",
        action: () => this.handleOpenDeleteDialog(true),
      }),
    ];
  };

  getColumnDefs = () => {
    const { t } = this.props;
    return [
      {
        field: "task_name",
        headerName: t("Task.TaskTitle"),
        minWidth: 140,
        cellRenderer: (params) => {
          const value = params.data && params.data.task_name;
          if (!this.handleValidateFields(value)) return null;
          if (String(value).trim().length > 10) {
            return (
              <HTMLTooltip
                placement="right"
                title={
                  <React.Fragment>
                    <Typography color="inherit">{value}</Typography>
                  </React.Fragment>
                }
              >
                <Typography color="inherit" sx={{ fontSize: "0.8125rem" }}>
                  {String(value).slice(0, 3) + "..."}
                </Typography>
              </HTMLTooltip>
            );
          }
          return value;
        },
      },
      {
        field: "taskType",
        headerName: t("Task.TaskType"),
        minWidth: 120,
      },
      {
        field: "status",
        headerName: t("Task.Status"),
        minWidth: 130,
        filter: "agSetColumnFilter",
        filterParams: {
          values: STATUS,
          defaultToNothingSelected: true,
          buttons: ["clear", "apply"],
          closeOnApply: true,
        },
        menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
        cellRenderer: (params) => {
          const value = params.data && params.data.status;
          if (!this.handleValidateFields(value)) return null;
          return (
            <Chip
              label={value}
              variant="outlined"
              size="medium"
              sx={{
                "&.MuiChip-root": {
                  color: this.renderStatusColor(value),
                  border: `2px solid ${this.renderStatusColor(value)}`,
                },
              }}
            />
          );
        },
      },
      {
        field: "priority",
        headerName: t("Task.Priority"),
        minWidth: 130,
        filter: "agSetColumnFilter",
        filterParams: {
          values: PRIORITY,
          defaultToNothingSelected: true,
          buttons: ["clear", "apply"],
          closeOnApply: true,
        },
        menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
        cellRenderer: (params) => {
          const value = params.data && params.data.priority;
          if (!this.handleValidateFields(value)) return null;
          return (
            <Chip
              label={value}
              variant="outlined"
              size="medium"
              sx={{
                "&.MuiChip-root": {
                  color: this.renderPriorityColor(value),
                  border: `2px solid ${this.renderPriorityColor(value)}`,
                },
              }}
            />
          );
        },
      },
      {
        field: "reminder_date",
        headerName: t("Task.ReminderDate"),
        minWidth: 150,
        valueFormatter: (params) =>
          this.handleValidateFields(params.value)
            ? this.convertToTimezone(params.value)
            : "",
      },
      {
        field: "due_date",
        headerName: t("Task.DueDate"),
        minWidth: 150,
        valueFormatter: (params) =>
          this.handleValidateFields(params.value)
            ? this.convertToTimezone(params.value)
            : "",
      },
      {
        field: "patientName",
        headerName: t("Task.Patient"),
        minWidth: 120,
      },
      {
        field: "userName",
        headerName: t("Task.User"),
        minWidth: 120,
      },
      {
        field: "selected_emails",
        headerName: t("Patient.Email"),
        minWidth: 200,
      },
      {
        field: "is_enabled",
        headerName: t("Patient.Enabled"),
        minWidth: 110,
        filter: "agSetColumnFilter",
        filterParams: {
          values: ["Yes", "No"],
          defaultToNothingSelected: true,
          buttons: ["clear", "apply"],
          closeOnApply: true,
        },
        menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
        valueGetter: (params) =>
          params.data && [true, "true"].includes(params.data.is_enabled)
            ? "Yes"
            : "No",
      },
      getActionsColumn({
        headerName: t("Task.Actions") || "Actions",
        onActionsClick: (event, params, menuHelpers) =>
          this.openRowActionsMenu(event, params, menuHelpers),
      }),
    ];
  };

  isHiddenAddTaskType = (item) => {
    if (!item) return true;
    if (item.id === TaskTypeEnum.RefillRequest) return true;
    if (item.id === TaskTypeEnum.PickupChangeRequest) return true;
    const name = String(item.name || "").toLowerCase();
    return name.indexOf("refill") !== -1 || name.indexOf("pickup change") !== -1;
  };

  getAddTaskTypeOptions = () => {
    const types = this.state.taskTypeList || [];
    const selectedId = this.state.selectedTaskTypeID;
    const keepSelected = this.state.operation !== "Add";
    return types.filter((item) => {
      if (!item) return false;
      if (this.isHiddenAddTaskType(item)) {
        return keepSelected && item.id === selectedId;
      }
      return true;
    });
  };

  getDefaultAddTaskType = () => {
    const types = this.state.taskTypeList || [];
    return (
      types.find((item) => !this.isHiddenAddTaskType(item)) ||
      types[0] ||
      null
    );
  };

  handleOpenTaskManagerDialog = () => {
    const defaultType = this.getDefaultAddTaskType();
    this.setState({
      openTaskManagerDialog: true,
      operation: "Add",
      selectedRowID: null,
      taskTitle: "",
      action: "Open",
      reminderDate: new Date(),
      dueDate: new Date(),
      status: "Open",
      priority: "Low",
      taskDetails: "",
      orderCode: "",
      filterStatusList: [],
      filterActionList: [],
      filterPriorityList: [],
      isTaskTitleRequired: false,
      isTaskDetailsRequired: false,
      isUserRequired: false,
      isPatientRequired: false,
      selectedUser: { id: "", name: "" },
      selectedPatient: { id: "", name: "" },
      selectedUserInputValue: "",
      selectedPatientInputValue: "",
      selectedUserID: "",
      selectedPatientID: "",
      selectedUserName: "",
      selectedPatientName: "",
      selectedTaskTypeID: (defaultType && defaultType.id) || "",
      selectedTaskName: (defaultType && defaultType.name) || "",
    });
    this.loadAssigneeSelectLists({ id: "" }, { id: "" });
  };

  handleCloseTaskManagerDialog = () => {
    this.setState({ openTaskManagerDialog: false });
  };

  handleChangeTaskType = (event) => {
    const { value } = event.target;
    let selectedTaskName = "";
    let findTask = this.state.taskTypeList.find(({ id }) => id === value);
    if (findTask) {
      selectedTaskName = findTask["name"];
    }
    this.setState({
      selectedTaskTypeID: event.target.value,
      selectedTaskName,
    });
  };

  handleChangeUsers = (event) => {
    const id = event && event.target ? event.target.value : "";
    const match = (this.state.userDataList || []).find(
      (user) => String(user.id) === String(id)
    );
    this.setState({
      selectedUserID: id || "",
      selectedUserName: (match && match.name) || "",
      selectedUser: match || { id: "", name: "" },
      isUserRequired: false,
    });
  };

  handleChangePatients = (event) => {
    const id = event && event.target ? event.target.value : "";
    const match = (this.state.patientDataList || []).find(
      (patient) => String(patient.id) === String(id)
    );
    this.setState({
      selectedPatientID: id || "",
      selectedPatientName: (match && match.name) || "",
      selectedPatient: match || { id: "", name: "" },
      isPatientRequired: false,
    });
  };

  renderLoaderIndicator = () => {
    const { classes } = this.props;
    return (
      this.state.loadingSpinner && (
        <Box component="div" className={classes["spinner"]}>
          <Box
            component="img"
            className={classes["spinnerImage"]}
            src={spinner}
            alt="loading"
          />
        </Box>
      )
    );
  };

  handleCloseContextMenuClick = (event) => {
    if (event && event.preventDefault) event.preventDefault();
    this.setState({ anchorEl: null, selectedRowID: null });
  };

  isPickupChangeRow = (row) =>
    row && row.task_type_id === TaskTypeEnum.PickupChangeRequest;

  openPickupChangeTaskDialog = (row, operation = "Edit") => {
    if (!row) return;
    this.setState(
      {
        selectedRowID: row.id,
        _contextRow: row,
        selectedRow: row,
        operation,
        openTaskManagerDialog: true,
        isTaskTitleRequired: false,
        isTaskDetailsRequired: false,
        isUserRequired: false,
        isPatientRequired: false,
      },
      () => {
        this.handleStates();
        this.loadAssigneeSelectLists(
          { id: row.user_id, name: row.userName },
          { id: row.patient_id, name: row.patientName }
        );
      }
    );
  };

  handleRowClicked = (params) => {
    const row = params && params.data;
    const colId =
      params &&
      params.column &&
      (params.column.getColId ? params.column.getColId() : params.column.colId);
    if (colId === "actions") return;
    if (!this.isPickupChangeRow(row)) return;
    this.openPickupChangeTaskDialog(row, "Edit");
  };

  handleStates = () => {
    let selectedData =
      this.state.data.find(({ id }) => id === this.state["selectedRowID"]) ||
      this.state._contextRow;
    if (!selectedData) return;
    this.setState({
      taskTitle: selectedData["task_name"],
      action: selectedData["action"],
      reminderDate: selectedData["reminder_date"],
      dueDate: selectedData["due_date"],
      selectedTaskTypeID: selectedData["task_type_id"],
      selectedTaskName: selectedData["taskType"],
      selectedUserID: selectedData["user_id"]
        ? String(selectedData["user_id"])
        : "",
      selectedUserName: selectedData["userName"] || "",
      status: selectedData["status"],
      priority: selectedData["priority"],
      taskDetails: selectedData["task_details"],
      selectedPatientID: selectedData["patient_id"]
        ? String(selectedData["patient_id"])
        : "",
      selectedPatientName: selectedData["patientName"] || "",
      orderCode: selectedData["order_code"],
      selectedEmails: selectedData["selected_emails"],
      isEanbled: selectedData["is_enabled"],
      selectedUser: {
        id: selectedData["user_id"] ? String(selectedData["user_id"]) : "",
        name: selectedData["userName"] || "",
      },
      selectedPatient: {
        id: selectedData["patient_id"] ? String(selectedData["patient_id"]) : "",
        name: selectedData["patientName"] || "",
      },
    });
  };

  handleOpenView = (event) => {
    const selectedObject =
      this.state["data"].find(({ id }) => id === this.state["selectedRowID"]) ||
      this.state._contextRow;
    this.setState({
      operation: "View",
      openTaskManagerDialog: true,
      isTaskTitleRequired: false,
      isTaskDetailsRequired: false,
      isUserRequired: false,
      isPatientRequired: false,
    });
    this.handleStates();
    if (selectedObject) {
      this.loadAssigneeSelectLists(
        { id: selectedObject.user_id, name: selectedObject.userName },
        { id: selectedObject.patient_id, name: selectedObject.patientName }
      );
    } else {
      this.loadAssigneeSelectLists();
    }
    this.handleCloseContextMenuClick(event);
  };

  handleOpenEdit = (event) => {
    const selectedObject =
      this.state["data"].find(({ id }) => id === this.state["selectedRowID"]) ||
      this.state._contextRow;
    if (!selectedObject) return;
    this.setState({
      operation: "Edit",
      openTaskManagerDialog: true,
      selectedRow: selectedObject,
      isTaskTitleRequired: false,
      isTaskDetailsRequired: false,
      isUserRequired: false,
      isPatientRequired: false,
    });
    this.handleStates();
    this.loadAssigneeSelectLists(
      { id: selectedObject.user_id, name: selectedObject.userName },
      { id: selectedObject.patient_id, name: selectedObject.patientName }
    );
    this.handleCloseContextMenuClick(event);
  };

  handleOpenDeleteDialog = (fromContextMenu) => {
    if (fromContextMenu && this.state["selectedRowID"]) {
      let selectedRow = this.state.data.filter(
        ({ id }) => id === this.state["selectedRowID"]
      );
      if (
        (!selectedRow || selectedRow.length === 0) &&
        this.state._contextRow
      ) {
        selectedRow = [this.state._contextRow];
      }

      this.setState({
        openDeleteDialog: true,
        confirmedDeleteList: selectedRow,
      });
    } else {
      // Toolbar bulk delete — ignore any leftover kebab selectedRowID
      // eslint-disable-next-line react/no-direct-mutation-state
      this.state.selectedRowID = null;
      let selectedRows = this.state.data.filter(
        ({ id }) => this.state["selectedData"].indexOf(id) > -1
      );
      this.setState({
        openDeleteDialog: true,
        confirmedDeleteList: selectedRows,
      });
    }
  };

  handleCloseDeleteDialog = (event) => {
    if (this.state["selectedRowID"]) {
      this.setState({
        openDeleteDialog: false,
        selectedRowID: null,
        confirmedDeleteList: null,
        selectedData: [],
      });
      this.handleCloseContextMenuClick(event);
    } else
      this.setState({
        openDeleteDialog: false,
        selectedRowID: null,
        confirmedDeleteList: null,
        selectedData: [],
      });
  };

  handleConfirmDelete = (event) => {
    if (this.state["selectedRowID"]) {
      this.handleConfirmDeleteTask(event, "Single");
    } else this.handleConfirmDeleteTask(event, "Multiple");
    this.setState({ openDeleteDialog: false });
  };

  handleConfirmDeleteTask = (event, ope) => {
    this.setState({ loadingSpinner: true });
    let URL =
      ope === "Single"
        ? `taskmanager/${this.state["selectedRowID"]}`
        : "taskmanager/" + this.state["selectedData"].join(",");
    fetch(process.env.REACT_APP_BASEURL + URL, {
      method: "DELETE",
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
        Authorization:
          "Bearer " +
          JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
            .jwtToken,
      },
    })
      .then((response) => {
        return response.json();
      })
      .then(async (result) => {
        if (result) {
          this.setState({
            selectedData: [],
            selectedRowID: null,
            loadingSpinner: false,
          });
          this.handleCloseContextMenuClick(event);
          await this.gettingTaskManagerDetailsFromDB();
          this.emitNotificationEvent(this.state.selectedUserID);
        }
      })
      .catch((error) => {
        this.setState({
          selectedData: [],
          selectedRowID: null,
          loadingSpinner: false,
        });
        log(
          "Error Ocurred while deleting the user automation details",
          JSON.stringify(error["message"])
        );
      });
  };

  emptyToNull = (value) => {
    if (value == null || value === "" || value === "null" || value === "undefined") {
      return null;
    }
    return value;
  };

  prepareTaskConfig = () => {
    return {
      taskTitle: this.state.taskTitle,
      action: this.state.action,
      reminderDate: this.emptyToNull(this.state.reminderDate),
      dueDate: this.emptyToNull(this.state.dueDate),
      taskTypeID: this.emptyToNull(this.state.selectedTaskTypeID),
      userID: this.emptyToNull(this.state.selectedUserID),
      parentAccountID: this.emptyToNull(
        sessionStorage.getItem("parentAccountId")
      ),
      status: this.state.status,
      priority: this.state.priority,
      taskDetails: this.state.taskDetails,
      patientID: this.emptyToNull(this.state.selectedPatientID),
      orderCode: this.state.orderCode,
    };
  };

  isEmptyHtml = (html) => {
    if (html == null || html === "") {
      return true;
    }
    const text = String(html)
      .replace(/<style[\s\S]*?<\/style>/gi, " ")
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/\s+/g, " ")
      .trim();
    return text === "";
  };

  handleTaskDetailsChange = (html) => {
    this.setState({
      taskDetails: html,
      isTaskDetailsRequired: false,
    });
  };

  handleSubmit = (event) => {
    const { selectedTaskTypeID, selectedEmails } = this.state;
    try {
      event && event.preventDefault();
      if(selectedTaskTypeID !== TaskTypeEnum.MonitorLiveAgents) {
        let validate = {
          isTaskTitleRequired: this.prepareTaskConfig().taskTitle,
          isTaskDetailsRequired: this.isEmptyHtml(
            this.prepareTaskConfig().taskDetails
          )
            ? ""
            : this.prepareTaskConfig().taskDetails,
          isUserRequired: this.prepareTaskConfig().userID,
          isPatientRequired: this.prepareTaskConfig().patientID,
        };
        validate = Object.entries(validate)
          .filter(([key, value]) => ["", null, undefined].includes(value))
          .reduce((other, [key, node]) => ({ ...other, [key]: node }), {});
        if (Object.keys(validate).length) {
          if (validate.isTaskTitleRequired === "")
            this.setState({ isTaskTitleRequired: true });
          else this.setState({ isTaskTitleRequired: false });
          if (validate.isTaskDetailsRequired === "")
            this.setState({ isTaskDetailsRequired: true });
          else this.setState({ isTaskDetailsRequired: false });
          if (validate.isUserRequired === "")
            this.setState({ isUserRequired: true });
          else this.setState({ isUserRequired: false });
          if (validate.isPatientRequired === "")
            this.setState({ isPatientRequired: true });
          else this.setState({ isPatientRequired: false });
          return true;
        }
  
        this.setState({ loadingSpinner: true });
        fetch(process.env.REACT_APP_BASEURL + "taskmanager", {
          method: "POST",
          withCredentials: true,
          headers: {
            Authorization:
              "Bearer " +
              JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
                .jwtToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(this.prepareTaskConfig()),
        })
          .then((response) => response.json())
          .then((response) => {
            this.setState({
              loadingSpinner: false,
              openTaskManagerDialog: false,
            });
          });
        toast.success(this.props.t('Toast.Success.TaskCreated'));
        this.gettingTaskManagerDetailsFromDB();
        this.emitNotificationEvent(this.state.selectedUserID);        
      } else {
        if([undefined, 'undefined', null, 'null', ''].includes(selectedEmails)) {
          this.setState({ isTaskEmailRequired: true });
        } else {
          const { parent_account_id, selectedTaskTypeID, selectedEmails, isEanbled } = this.state;
          this.setState({ loadingSpinner: true });
          let body = {
            parentAccountID: parent_account_id,
            taskTypeID: selectedTaskTypeID,
            selected_emails: selectedEmails,
            isEanbled: isEanbled,
            status: 'Open'
          }
          fetch(process.env.REACT_APP_BASEURL + "taskmanager", {
            method: "POST",
            withCredentials: true,
            headers: {
              Authorization:
                "Bearer " +
                JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
                  .jwtToken,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
          })
            .then((response) => response.json())
            .then((response) => {
              this.setState({
                loadingSpinner: false,
                openTaskManagerDialog: false,
              });
              toast.success(this.props.t('Toast.Success.TaskCreated'));
              this.gettingTaskManagerDetailsFromDB();
              this.setState({ selectedRow: {} });
              // setTimeout(async () => {
              //   toast.success(this.props.t('Toast.Success.TaskCreated'));
              //   await this.callInitialAPIs()
              //   this.clearFields();
              // }, 200)
            });
        }
      }
    } catch (SubmitException) {
      this.setState({
        loadingSpinner: false,
        openTaskManagerDialog: false,
      });
      log(
        "(TaskManager): Error ocurred while Submitting the task details!",
        SubmitException
      );
      toast.error(SubmitException);
    }
  };

  handleUpdate = (event) => {
    try {
      const { selectedTaskTypeID, selectedEmails } = this.state;
      if (selectedTaskTypeID === TaskTypeEnum.PickupChangeRequest) {
        event && event.preventDefault();
        this.updatePickupChangeTaskStatus();
        return;
      }
      if(selectedTaskTypeID !== TaskTypeEnum.MonitorLiveAgents) {
        event && event.preventDefault();
        let validate = {
          isTaskTitleRequired: this.prepareTaskConfig().taskTitle,
          isTaskDetailsRequired: this.isEmptyHtml(
            this.prepareTaskConfig().taskDetails
          )
            ? ""
            : this.prepareTaskConfig().taskDetails,
          isUserRequired: this.prepareTaskConfig().userID,
          isPatientRequired: this.prepareTaskConfig().patientID,
        };
        validate = Object.entries(validate)
          .filter(([key, value]) => ["", null, undefined].includes(value))
          .reduce((other, [key, node]) => ({ ...other, [key]: node }), {});
        if (Object.keys(validate).length) {
          if (validate.isTaskTitleRequired === "")
            this.setState({ isTaskTitleRequired: true });
          else this.setState({ isTaskTitleRequired: false });
          if (validate.isTaskDetailsRequired === "")
            this.setState({ isTaskDetailsRequired: true });
          else this.setState({ isTaskDetailsRequired: false });
          if (validate.isUserRequired === "")
            this.setState({ isUserRequired: true });
          else this.setState({ isUserRequired: false });
          if (validate.isPatientRequired === "")
            this.setState({ isPatientRequired: true });
          else this.setState({ isPatientRequired: false });
          return true;
        }
        this.setState({ loadingSpinner: true });
        let config = this.prepareTaskConfig();
        config["id"] = this.state["selectedRow"]["id"];
        fetch(process.env.REACT_APP_BASEURL + "taskmanager", {
          method: "PUT",
          withCredentials: true,
          headers: {
            Authorization:
              "Bearer " +
              JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
                .jwtToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(config),
        })
          .then((response) => response.json())
          .then((response) => {
            if (!response || response.success === false) {
              this.setState({ loadingSpinner: false });
              toast.error(
                (response && response.message) ||
                  this.props.t("Toast.Error.TaskUpdated", {
                    defaultValue: "Task could not be updated.",
                  })
              );
              return;
            }
            this.setState({
              loadingSpinner: false,
              openTaskManagerDialog: false,
            });
            toast.success(this.props.t('Toast.Success.TaskUpdated'));
            invalidateQueueCache();
            this.notifyPickupTaskStatusChanged();
            this.loadQueueSummary();
            this.gettingTaskManagerDetailsFromDB();
            this.setState({ selectedRow: {} });
            this.emitNotificationEvent(this.state.selectedUserID);
          });
      } else {
        if([undefined, 'undefined', null, 'null', ''].includes(selectedEmails)) {
          this.setState({ isTaskEmailRequired: true });
        } else {
          const { parent_account_id, selectedTaskTypeID, selectedEmails, isEanbled } = this.state;
          this.setState({ loadingSpinner: true });
          let body = {
            id: this.state["selectedRow"]["id"],
            parentAccountID: parent_account_id,
            taskTypeID: selectedTaskTypeID,
            selected_emails: selectedEmails,
            isEanbled: isEanbled,
            status: 'Open'
          }
          fetch(process.env.REACT_APP_BASEURL + "taskmanager", {
            method: "PUT",
            withCredentials: true,
            headers: {
              Authorization:
                "Bearer " +
                JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
                  .jwtToken,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
          })
            .then((response) => response.json())
            .then((response) => {
              this.setState({
                loadingSpinner: false,
                openTaskManagerDialog: false,
              });
              this.gettingTaskManagerDetailsFromDB();
              this.setState({ selectedRow: {} });
              toast.success(this.props.t('Toast.Success.TaskUpdated'));
              // setTimeout(async () => {
              //   toast.success(this.props.t('Toast.Success.TaskUpdated'));
              //   await this.callInitialAPIs();
              //   this.clearFields();
              // }, 200);
            });
        }
      }
    } catch (SubmitException) {
      this.setState({
        loadingSpinner: false,
        openTaskManagerDialog: false,
      });
      log(
        "(TaskManager): Error ocurred while Updating the task details!",
        SubmitException
      );
    }
  };

  updatePickupChangeTaskStatus = () => {
    const selectedRow = this.state.selectedRow || {};
    const taskId = selectedRow.id || this.state.selectedRowID;
    if (!taskId) return;
    const updateBody = {
      ...this.prepareTaskConfig(),
      id: taskId,
      taskTitle: selectedRow.task_name || this.state.taskTitle,
      action: selectedRow.action || this.state.action || "",
      taskTypeID: this.emptyToNull(
        selectedRow.task_type_id || this.state.selectedTaskTypeID
      ),
      userID: this.emptyToNull(selectedRow.user_id || this.state.selectedUserID),
      parentAccountID: this.emptyToNull(
        selectedRow.parent_account_id ||
          sessionStorage.getItem("parentAccountId")
      ),
      patientID: this.emptyToNull(
        selectedRow.patient_id || this.state.selectedPatientID
      ),
      taskDetails: selectedRow.task_details || this.state.taskDetails || "",
      orderCode: selectedRow.order_code || this.state.orderCode || "",
      priority: selectedRow.priority || this.state.priority || "",
      status: this.state.status,
    };

    this.setState({ loadingSpinner: true });
    fetch(process.env.REACT_APP_BASEURL + "taskmanager", {
      method: "PUT",
      withCredentials: true,
      headers: {
        Authorization:
          "Bearer " +
          JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
            .jwtToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updateBody),
    })
      .then((response) => response.json())
      .then((response) => {
        if (!response || response.success === false) {
          this.setState({ loadingSpinner: false });
          toast.error(
            (response && response.message) ||
              this.props.t("Toast.Error.TaskUpdated", {
                defaultValue: "Task could not be updated.",
              })
          );
          return;
        }
        this.notifyPickupTaskStatusChanged();
        this.setState({
          loadingSpinner: false,
          openTaskManagerDialog: false,
          selectedRow: {},
        });
        toast.success(this.props.t("Toast.Success.TaskUpdated"));
        this.loadQueueSummary();
        this.gettingTaskManagerDetailsFromDB();
      })
      .catch((error) => {
        console.error("[TaskManager][PickupChange] update failed", {
          taskId,
          error,
        });
        this.setState({ loadingSpinner: false });
        toast.error(error);
      });
  };

  notifyPickupTaskStatusChanged = () => {
    invalidateQueueCache();
    const timestamp = String(Date.now());
    try {
      localStorage.setItem(PICKUP_TASK_SYNC_KEY, timestamp);
    } catch (error) {
      log("(TaskManager): Unable to write pickup task sync marker.", error);
    }

    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
      window.dispatchEvent(
        new CustomEvent("pickup-change-tasks-updated", {
          detail: { updatedAt: timestamp },
        })
      );
    }
  };

  renderStatusColor = (value) => {
    if (value === "Open") {
      return "#6554C1";
    }
    if (value === "InProgress") {
      return "#FFAB00";
    }
    if (value === "Complete") {
      return "#36B37E";
    }
    if (value === "Cancelled") {
      return "#6B778C";
    }
  };

  renderPriorityColor = (value) => {
    if (value === "Low") {
      return "#0065FF";
    }
    if (value === "Medium") {
      return "#FFAB00";
    }
    if (["High", "Critical"].includes(value)) {
      return "#FF5630";
    }
  };

  renderActionColor = (value) => {
    if (value === "Open") {
      return "#6554C1";
    }
    if (value === "Complete") {
      return "#36B37E";
    }
    if (value === "Delete") {
      return "#FF5959";
    }
    if (value === "Close") {
      return "#FF5630";
    }
  };

  handleValidateFields = (value) => {
    if (!["", null, "null", undefined, "undefined"].includes(value)) {
      return value;
    }
    return null;
  };

  convertToTimezone = (date) => {
    return moment(date)
      .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
      .format("MM/DD/YYYY HH:mm");
  };

  parseTaskDetails = (taskDetails) => {
    if (!taskDetails || typeof taskDetails !== "string") {
      return null;
    }

    try {
      return JSON.parse(taskDetails);
    } catch (error) {
      return null;
    }
  };

  getPickupChangeTaskDetails = (taskDetails) => {
    const details = this.parseTaskDetails(taskDetails);
    if (!details || details.type !== "pickup_change_request") {
      return null;
    }

    const timezone = getTaskDetailTimezone(details.timezone);
    const requestedAt = details.requested_at
      ? moment(details.requested_at).tz(timezone).format("MM/DD/YYYY hh:mm:ss A")
      : "N/A";

    return {
      requestedChange: details.requested_change || "N/A",
      fromLocation: details.from_location || "N/A",
      toLocation: details.to_location || "N/A",
      customerName: details.customer_name || "N/A",
      bagNumber: details.bag_number || "N/A",
      pickupCode: details.pickup_code || "N/A",
      stockCode: details.stock_code || "N/A",
      action: details.action || "N/A",
      instructionSummary: details.instruction_summary || "",
      instructions: Array.isArray(details.instructions) ? details.instructions : [],
      source: details.source || "N/A",
      requestedAt,
    };
  };

  getPickupChangeStatusColor = (status) => {
    switch (status) {
      case "Complete":
        return { color: "#166534", backgroundColor: "#DCFCE7", borderColor: "#86EFAC" };
      case "InProgress":
        return { color: "#1D4ED8", backgroundColor: "#DBEAFE", borderColor: "#93C5FD" };
      case "Cancelled":
        return { color: "#991B1B", backgroundColor: "#FEE2E2", borderColor: "#FCA5A5" };
      default:
        return { color: "#92400E", backgroundColor: "#FEF3C7", borderColor: "#FCD34D" };
    }
  };

  renderPickupChangeTaskDetailsCard = (taskDetails, { editableStatus = false } = {}) => {
    const details = this.getPickupChangeTaskDetails(taskDetails);
    if (!details) {
      return null;
    }
    const statusStyle = this.getPickupChangeStatusColor(this.state.status);
    const statusLabel = this.state.status === "InProgress" ? "In Progress" : this.state.status;
    const orderIdentifier = details.stockCode !== "N/A"
      ? details.stockCode
      : details.pickupCode;
    const instructionSummary = details.instructionSummary ||
      `This order ${orderIdentifier} is in location ${details.bagNumber}. Here’s what you need to do.`;
    const instructions = details.instructions.length > 0
      ? details.instructions
      : (() => {
          const action = String(details.action || details.requestedChange || '').toLowerCase();
          const requestedChange = String(details.requestedChange || '').toLowerCase();
          const targetLocation = details.toLocation || 'selected location';
          if (action.includes('curbside') || requestedChange.includes('to curbside')) {
            return [
              'Remove the current assignment.',
              `Assign the prescriptions to Curbside storage at ${targetLocation}.`,
              'Clear any parking/vehicle arrival details if the order already arrived.',
              'When done, mark this task complete.',
            ];
          }
          if (action.includes('locker') || action.includes('kiosk') || requestedChange.includes('to kiosk')) {
            return [
              requestedChange.includes('curbside to')
                ? 'Remove the Curbside assignment'
                : 'Remove the Counter assignment',
              `Assign the prescriptions to a new bin in kiosk ${targetLocation}`,
              'When done, mark this task complete.',
            ];
          }
          return [
            requestedChange.includes('curbside to')
              ? 'Remove the Curbside assignment.'
              : 'Remove the Kiosk assignment.',
            `Assign the prescriptions to counter location ${targetLocation}.`,
            'When done, mark this task complete.',
          ];
        })();

    const rows = [
      ["Customer", details.customerName],
      ["Stock code", details.stockCode],
      ["Pickup code", details.pickupCode],
      ["Bag number", details.bagNumber],
      ["From location", details.fromLocation],
      ["To location", details.toLocation],
      ["Requested from", details.source],
      ["Requested at", details.requestedAt],
    ];

    return (
      <Box
        sx={{
          border: "1px solid #E2E8F0",
          borderRadius: "8px",
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            backgroundColor: "#F8FAFC",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#0F172A" }}>
              Pickup change request
            </Typography>
            <Chip
              label={statusLabel || "Open"}
              size="small"
              sx={{
                height: "24px",
                fontSize: "0.72rem",
                fontWeight: 700,
                color: statusStyle.color,
                backgroundColor: statusStyle.backgroundColor,
                border: `1px solid ${statusStyle.borderColor}`,
              }}
            />
          </Box>
          <Typography sx={{ fontSize: "0.82rem", color: "#475569", mt: 0.5 }}>
            {details.requestedChange}
          </Typography>
        </Box>
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E2E8F0", backgroundColor: "#FFF7ED" }}>
          <Typography sx={{ fontSize: "0.88rem", color: "#7C2D12", fontWeight: 700, mb: 1 }}>
            {instructionSummary}
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.25, color: "#7C2D12" }}>
            {instructions.map((instruction, index) => (
              <Box
                component="li"
                key={`${instruction}-${index}`}
                sx={{ fontSize: "0.84rem", lineHeight: 1.55, mb: 0.5 }}
              >
                {instruction}
              </Box>
            ))}
          </Box>
        </Box>
        <Grid container>
          {rows.map(([label, value]) => (
            <Grid item xs={12} sm={6} key={label}>
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  minHeight: "62px",
                  borderBottom: "1px solid #F1F5F9",
                  borderRight: { sm: "1px solid #F1F5F9" },
                }}
              >
                <Typography sx={{ fontSize: "0.72rem", color: "#64748B", textTransform: "uppercase" }}>
                  {label}
                </Typography>
                <Typography sx={{ fontSize: "0.92rem", color: "#0F172A", fontWeight: 600, mt: 0.35 }}>
                  {value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        {editableStatus && (
          <Box sx={{ px: 2, py: 1.5, backgroundColor: "#F8FAFC", borderTop: "1px solid #E2E8F0" }}>
            <FormControl variant="standard" fullWidth>
              <InputLabel id="pickup-change-status-label">Status</InputLabel>
              <Select
                labelId="pickup-change-status-label"
                value={this.state.status || "Open"}
                onChange={(event) => {
                  this.setState({ status: event.target.value });
                }}
              >
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="InProgress">In Progress</MenuItem>
                <MenuItem value="Complete">Complete</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>
    );
  };

  // getTextBoxInputValue = (input) => {
  //   return input.map((itm) => itm.label).join(";");
  // };

  handleSelectionChange = (result) => {
    const valueToSave = result.map((itm) => itm.label).join(";");
    this.setState({ currentSelection: valueToSave });
  };

  fetchAccounts = () => {
    fetch(process.env.REACT_APP_BASEURL + 'accounts', {
      method: 'GET',
      withCredentials: true,
      headers: {
        Authorization:
          'Bearer ' +
          JSON.parse(sessionStorage.getItem('user')).signInUserSession.idToken
            .jwtToken,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((accounts) => {
        const accountValues = accounts.map((account) => {
          return { value: account.id, label: account.name };
        });
        this.setState({
          accountValues,
          parent_account_id: accountValues[0].value,
        });
      });
  }

  handleSwitchChangeIsEnableTask = (event) => {
    this.setState({ isEanbled: event.target.checked });
  };

  formatPreviewDate = (value) => {
    if (!value) {
      return "—";
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("MMM D, YYYY") : "—";
  };

  getPreviewAssignedName = () => {
    const user = this.state.selectedUser;
    if (user && typeof user === "object") {
      return user.name || user.value || "";
    }
    return this.state.selectedUserName || "";
  };

  getTaskTypeHelp = (typeId, typeName) => {
    const { t } = this.props;
    const name = String(typeName || "").trim().toLowerCase();
    if (
      typeId === TaskTypeEnum.PickupChangeRequest ||
      name.indexOf("pickup change") !== -1
    ) {
      return t("Task.TypeHelp.PickupChangeRequest", {
        defaultValue:
          "A patient asked to change pickup or delivery method (Kiosk, Counter, Curbside, Bedside, Courier). These tasks appear in Delivery Changes.",
      });
    }
    if (
      typeId === TaskTypeEnum.MissedConsultationCall ||
      name.indexOf("missed consultation") !== -1
    ) {
      return t("Task.TypeHelp.MissedConsultationCall", {
        defaultValue:
          "A consultation was missed. Reach the patient and close the follow-up. These tasks are not part of the workflow queue.",
      });
    }
    if (
      typeId === TaskTypeEnum.MonitorLiveAgents ||
      name.indexOf("monitor live") !== -1
    ) {
      return t("Task.TypeHelp.MonitorLiveAgents", {
        defaultValue:
          "Sends email alerts for live-agent monitoring. Enter recipient emails and keep the task enabled to run it.",
      });
    }
    if (
      typeId === TaskTypeEnum.DeliveryUpdate ||
      name.indexOf("delivery update") !== -1
    ) {
      return t("Task.TypeHelp.DeliveryUpdate", {
        defaultValue:
          "A new Counter, Courier, Curbside, Bedside, or Kiosk order that still needs work. These tasks appear in New Orders, or Due Today if they have a due date.",
      });
    }
    if (
      typeId === TaskTypeEnum.RefillRequest ||
      name.indexOf("refill") !== -1
    ) {
      return t("Task.TypeHelp.RefillRequest", {
        defaultValue:
          "A patient or pharmacist requested a refill. Review and fill the prescription. Refills are not New Orders. They appear in Due Today if they have a due date.",
      });
    }
    if (name.indexOf("new patient") !== -1 || name.indexOf("enrollment") !== -1) {
      return t("Task.TypeHelp.NewPatientEnrollment", {
        defaultValue:
          "Enroll a new patient and complete their setup. These tasks are not New Orders.",
      });
    }
    if (
      typeId === TaskTypeEnum.AppointmentRequest ||
      name.indexOf("appointment") !== -1
    ) {
      return t("Task.TypeHelp.AppointmentRequest", {
        defaultValue:
          "A patient requested an appointment. Confirm the time and follow up. These tasks appear in Due Today if they have a due date.",
      });
    }
    return t("Task.TypeHelp.Default", {
      defaultValue:
        "Choose the type that matches this work so it shows in the right queue.",
    });
  };

  renderTaskPreview = () => {
    const { t } = this.props;
    const {
      taskTitle,
      reminderDate,
      dueDate,
      taskTypeList,
      selectedTaskTypeID,
      selectedTaskName,
      status,
      priority,
      taskDetails,
      selectedPatient,
      selectedPatientName,
      selectedEmails,
      orderCode,
      accountValues,
      parent_account_id,
      isEanbled,
    } = this.state;
    const typeItem =
      (taskTypeList || []).find((ele) => ele && ele.id === selectedTaskTypeID) ||
      null;
    const typeName = (typeItem && typeItem.name) || selectedTaskName || "—";
    const typeHelp = this.getTaskTypeHelp(selectedTaskTypeID, typeName);
    const assigned = this.getPreviewAssignedName();
    const patient =
      (selectedPatient && selectedPatient.name) || selectedPatientName || "";
    const accountItem =
      (accountValues || []).find(
        (account) => account && account.value === parent_account_id
      ) || null;
    const isMonitor = selectedTaskTypeID === TaskTypeEnum.MonitorLiveAgents;
    const priorityColors = {
      Low: { bg: "#ecfeff", color: "#0e7490", border: "#a5f3fc" },
      Medium: { bg: "#fffbeb", color: "#b45309", border: "#fde68a" },
      High: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
      Critical: { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
    };
    const statusColors = {
      Open: { bg: "#ecfeff", color: "#0e7490" },
      InProgress: { bg: "#eff6ff", color: "#1d4ed8" },
      Complete: { bg: "#ecfdf5", color: "#047857" },
      Cancelled: { bg: "#f1f5f9", color: "#475569" },
    };
    const pStyle = priorityColors[priority] || priorityColors.Low;
    const sStyle = statusColors[status] || statusColors.Open;
    const row = (label, value) => (
      <Box sx={{ mb: 1.35 }}>
        <Typography
          sx={{
            fontSize: 11,
            color: "#64748b",
            fontWeight: 700,
            letterSpacing: "0.02em",
            mb: 0.25,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: 13.5,
            color: value ? "#0f172a" : "#94a3b8",
            fontWeight: 500,
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
          }}
        >
          {value || "—"}
        </Typography>
      </Box>
    );

    return (
      <Box>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "#64748b",
            mb: 1.5,
          }}
        >
          {t("Task.Preview", { defaultValue: "Preview" })}
        </Typography>
        <Box
          sx={{
            backgroundColor: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: "14px",
            p: 2.5,
            boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.05rem",
              color: "#0f172a",
              mb: 1.25,
              wordBreak: "break-word",
            }}
          >
            {taskTitle ||
              t("Task.Untitled", { defaultValue: "Untitled task" })}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
            <Chip
              size="small"
              label={typeName}
              sx={{ fontWeight: 700, backgroundColor: "#e0f2fe", color: "#0369a1" }}
            />
            {isMonitor ? null : (
              <Chip
                size="small"
                label={statusDisplay(status)}
                sx={{
                  fontWeight: 700,
                  backgroundColor: sStyle.bg,
                  color: sStyle.color,
                }}
              />
            )}
            {isMonitor ? null : (
              <Chip
                size="small"
                label={priority}
                sx={{
                  fontWeight: 700,
                  backgroundColor: pStyle.bg,
                  color: pStyle.color,
                  border: "1px solid " + pStyle.border,
                }}
              />
            )}
          </Box>
          {typeHelp ? (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: "10px",
                backgroundColor: "#f0f9ff",
                border: "1px solid #bae6fd",
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "#0369a1",
                  mb: 0.4,
                }}
              >
                {t("Task.TypeHelpLabel", { defaultValue: "About this type" })}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: "#0c4a6e",
                  lineHeight: 1.45,
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                }}
              >
                {typeHelp}
              </Typography>
            </Box>
          ) : null}
          {isMonitor ? (
            <React.Fragment>
              {row(
                t("Accounts.Account"),
                accountItem && accountItem.label
              )}
              {row(t("Automation.Email"), selectedEmails)}
              {row(
                t("Task.EnabledLabel", { defaultValue: "Enabled" }),
                isEanbled
                  ? t("Assign.YesButton")
                  : t("Assign.NoButton")
              )}
            </React.Fragment>
          ) : (
            <React.Fragment>
              {row(
                t("Task.AssignedToLabel"),
                assigned ||
                  t("Task.Unassigned", { defaultValue: "Unassigned" })
              )}
              {row(
                t("Task.ReminderDateLabel"),
                this.formatPreviewDate(reminderDate)
              )}
              {row(t("Task.DueDateLabel"), this.formatPreviewDate(dueDate))}
              {row(t("Task.PatientLabel"), patient)}
              {row(t("Task.OrderCode"), orderCode)}
              <Box sx={{ mb: 1.35 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "#64748b",
                    fontWeight: 700,
                    letterSpacing: "0.02em",
                    mb: 0.25,
                  }}
                >
                  {t("Task.Details", { defaultValue: "Details" })}
                </Typography>
                {this.isEmptyHtml(taskDetails) ? (
                  <Typography
                    sx={{
                      fontSize: 13.5,
                      color: "#94a3b8",
                      fontWeight: 500,
                    }}
                  >
                    {t("Task.NoDetails", { defaultValue: "No details yet" })}
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      fontSize: 13.5,
                      color: "#0f172a",
                      fontWeight: 500,
                      wordBreak: "break-word",
                      "& p": { margin: "0 0 8px" },
                      "& ul, & ol": { margin: "0 0 8px", paddingLeft: "1.2rem" },
                      "& a": { color: "#0369a1" },
                      "& img": { maxWidth: "100%", height: "auto" },
                    }}
                    dangerouslySetInnerHTML={{ __html: taskDetails }}
                  />
                )}
              </Box>
            </React.Fragment>
          )}
        </Box>
      </Box>
    );
  };

  clearFields = () => {
    this.setState = ({
      openTaskManagerDialog: false,
      taskTitle: "",
      action: "Open",
      reminderDate: new Date(),
      dueDate: new Date(),
      userDataList: [],
      selectedUserID: "",
      selectedUserName: "",
      taskTypeList: [],
      selectedTaskTypeID: "",
      selectedTaskName: "",
      status: "Open",
      priority: "Low",
      patientDataList: [],
      selectedPatientID: "",
      selectedPatientName: "",
      loadingSpinner: false,
      operation: "Add",
      taskDetails: "",
      orderCode: "",
      totalRecords: 0,
      data: [],
      isDataEmpty: false,
      selectedData: [],
      sortID: null,
      anchorEl: null,
      selectedRowID: null,
      openDeleteDialog: false,
      confirmedDeleteList: [],
      filterStatusList: [],
      filterActionList: [],
      filterPriorityList: [],
      openUserAutoComplete: false,
      openPatientAutoComplete: false,
      isTaskTitleRequired: false,
      isTaskDetailsRequired: false,
      isUserRequired: false,
      isPatientRequired: false,
      selectedUser: "",
      selectedUserInputValue: "",
      selectedPatient: "",
      selectedPatientInputValue: "",
      locale: localStorage.getItem('i18nextLng') === 'en' ? enLocale : frLocale,
      currentSelection: null,
      initialValue: [],
      accountValues: [],
      parent_account_id: null,
      selectedEmails: null,
      isEanbled: true,
      isTaskEmailRequired: false
    });
  }

  callInitialAPIs = async () => {
    this.gettingTaskManagerTypesList();
    this.fetchAccounts();
  }

  render() {
    const {
      openTaskManagerDialog,
      taskTitle,
      reminderDate,
      dueDate,
      userDataList,
      taskTypeList,
      selectedTaskTypeID,
      status,
      priority,
      patientDataList,
      operation,
      orderCode,
      taskDetails,
      selectedData,
      openDeleteDialog,
      confirmedDeleteList,
      isTaskTitleRequired,
      isTaskDetailsRequired,
      isUserRequired,
      isPatientRequired,
      selectedUserID,
      selectedPatientID,
      locale,
      selectedEmails,
      isTaskEmailRequired,
      activeLane,
      queueSummary,
      queueSummaryLoading,
    } = this.state;
    const addTaskTypeOptions = this.getAddTaskTypeOptions();
    const { classes, t } = this.props;
    const readOnly = operation === "View";
    const isPickupChangeRequest =
      selectedTaskTypeID === TaskTypeEnum.PickupChangeRequest;
    const pickupChangeDetailsCard = isPickupChangeRequest
      ? this.renderPickupChangeTaskDetailsCard(taskDetails, {
          editableStatus: !readOnly,
        })
      : null;
    const usePickupChangeLayout = Boolean(pickupChangeDetailsCard);
    const selectedTypeItem =
      (taskTypeList || []).find(
        (item) => item && item.id === selectedTaskTypeID
      ) || null;
    const selectedTypeName =
      (selectedTypeItem && selectedTypeItem.name) ||
      this.state.selectedTaskName ||
      "";

    return (
      <Box component="div">
        {this.renderLoaderIndicator()}
        <Box component="div" sx={{ width: "100%" }}>
          <Dialog
            maxWidth={false}
            fullWidth={true}
            open={openTaskManagerDialog || false}
            onClose={this.handleCloseTaskManagerDialog}
            aria-labelledby="form-dialog-title"
            disableEnforceFocus
            disableAutoFocus
            PaperProps={{
              sx: {
                width: "92vw",
                maxWidth: "1320px",
                minWidth: 0,
                maxHeight: "calc(100vh - 24px)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxSizing: "border-box",
                borderRadius: "16px",
              },
            }}
          >
            {this.renderLoaderIndicator()}
            <BootstrapDialogTitle
              id="customized-dialog-title"
              onDialogClose={this.handleCloseTaskManagerDialog}
            >
              {operation} {t("Task.Task")}
            </BootstrapDialogTitle>
            <DialogContent
              dividers
              sx={{ p: 0, overflow: "hidden", flex: 1, minHeight: 0 }}
            >
              {usePickupChangeLayout ? (
                <Box sx={{ p: 3 }}>{pickupChangeDetailsCard}</Box>
              ) : (
                <Box
                    sx={{
                      display: "flex",
                      alignItems: "stretch",
                      flex: 1,
                      minHeight: 0,
                      height: "100%",
                      width: "100%",
                      overflow: "hidden",
                      flexDirection: { xs: "column", md: "row" },
                    }}
                >
                  <Box
                    sx={{
                      flex: "1 1 58%",
                      minWidth: 0,
                      maxWidth: "100%",
                      p: "20px 28px 28px",
                      overflowX: "hidden",
                      overflowY: "auto",
                      boxSizing: "border-box",
                      color: "#0f172a",
                      "& .MuiGrid-container": {
                        width: "100%",
                        maxWidth: "100%",
                        marginLeft: 0,
                        marginRight: 0,
                      },
                      "& .MuiInput-input, & .MuiSelect-select": {
                        color: "#0f172a",
                        fontWeight: 500,
                      },
                      "& .MuiInputLabel-root": {
                        color: "#64748b",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#0369a1",
                      },
                      "& .MuiFormHelperText-root": {
                        color: "#64748b",
                      },
                    }}
                  >
                <React.Fragment>
              {selectedTaskTypeID !== TaskTypeEnum.MonitorLiveAgents ? (<Grid container>
                <Grid item xs={12}>
                  <FormControl variant="standard" fullWidth>
                    <TextField
                      id="standard-basic"
                      label={t("Task.EnterYourTask")}
                      variant="standard"
                      value={taskTitle}
                      placeholder={t("Task.Untitled", {
                        defaultValue: "Untitled task",
                      })}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        "& .MuiInput-input": {
                          color: "#0f172a",
                          fontWeight: 500,
                        },
                      }}
                      onChange={(event) =>
                        this.setState({
                          taskTitle: event.target.value,
                          isTaskTitleRequired: false,
                        })
                      }
                      disabled={Boolean(readOnly)}
                      error={isTaskTitleRequired}
                      helperText={
                        isTaskTitleRequired && t("Task.Validate.EnterTaskTitle")
                      }
                    />
                  </FormControl>
                </Grid>
              </Grid>) : (
                <Selector
                  id='parent_account_id'
                  label={t('Accounts.Account')}
                  values={this.state.accountValues}
                  selected={this.state.parent_account_id}
                  onBlur={this.handleChange}
                  readOnly={readOnly}
                  disableWhenViewSelect={readOnly}
                />
              )}
              {selectedTaskTypeID !== TaskTypeEnum.MonitorLiveAgents ? (
              <Grid container sx={{ mt: "10px", width: "100%" }}>
                <Grid item xs={6} sx={TASK_COL_LEFT}>
                  <FormControl fullWidth>
                    <LocalizationProvider dateAdapter={DateAdapter} locale={locale}>
                      <DesktopDatePicker
                        label={t("Task.ReminderDateLabel")}
                        value={reminderDate}
                        onChange={(date) =>
                          this.setState({ reminderDate: date })
                        }
                        disabled={Boolean(readOnly)}
                        renderInput={(params) => (
                          <TextField
                            label={t("Task.ReminderDateLabel")}
                            variant="standard"
                            fullWidth
                            {...params}
                            sx={TASK_DATE_FIELD_SX}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sx={TASK_COL_RIGHT}>
                  <FormControl fullWidth>
                    <LocalizationProvider dateAdapter={DateAdapter} locale={locale}>
                      <DesktopDatePicker
                        label={t("Task.DueDateLabel")}
                        value={dueDate}
                        onChange={(date) => this.setState({ dueDate: date })}
                        disabled={Boolean(readOnly)}
                        renderInput={(params) => (
                          <TextField
                            label={t("Task.DueDateLabel")}
                            variant="standard"
                            fullWidth
                            {...params}
                            sx={TASK_DATE_FIELD_SX}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </FormControl>
                </Grid>
              </Grid>) : null}
              <Grid container sx={{ mt: "12px", width: "100%", alignItems: "flex-start" }}>
                <Grid item xs={6} sx={TASK_COL_LEFT}>
                  <FormControl variant="standard" fullWidth>
                    <InputLabel id="demo-simple-select-standard-label">
                      {t("Task.TypeLabel")}
                    </InputLabel>
                    <Select
                      labelId="action-simple-select-standard-label"
                      id="action-simple-select-standard"
                      value={selectedTaskTypeID}
                      onChange={(event) => this.handleChangeTaskType(event)}
                      label="Type"
                      sx={{
                        ".MuiInput-input": {
                          color: "#0f172a",
                          fontWeight: "500",
                        },
                      }}
                      disabled={Boolean(readOnly)}
                      renderValue={(value) => {
                        const match = (taskTypeList || []).find(
                          (item) => item && item.id === value
                        );
                        return (match && match.name) || selectedTypeName || "";
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 420,
                            maxWidth: 420,
                          },
                        },
                      }}
                    >
                      {addTaskTypeOptions &&
                        addTaskTypeOptions.length > 0 &&
                        addTaskTypeOptions.map((ele, index) => (
                          <MenuItem
                            key={index}
                            value={ele["id"]}
                            selected={ele["id"] === selectedTaskTypeID}
                            sx={{
                              color: "#0f172a !important",
                              fontWeight: "400",
                              alignItems: "flex-start",
                              whiteSpace: "normal",
                              py: 1.25,
                            }}
                          >
                            <Box>
                              <Box
                                sx={{
                                  fontWeight: 600,
                                  fontSize: 14,
                                  color: "#0f172a",
                                  lineHeight: 1.3,
                                }}
                              >
                                {ele["name"]}
                              </Box>
                              <Box
                                sx={{
                                  mt: 0.35,
                                  fontSize: 12,
                                  color: "#64748b",
                                  lineHeight: 1.4,
                                }}
                              >
                                {this.getTaskTypeHelp(ele["id"], ele["name"])}
                              </Box>
                            </Box>
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                {selectedTaskTypeID !== TaskTypeEnum.MonitorLiveAgents ? (
                <Grid item xs={6} sx={TASK_COL_RIGHT}>
                  <SearchableSelect
                    label={t("Task.AssignedToLabel")}
                    items={userDataList || []}
                    value={selectedUserID ? String(selectedUserID) : ""}
                    onChange={this.handleChangeUsers}
                    placeholder="Search users..."
                    id="task-assigned-to-select"
                    fullWidth
                    variant="standard"
                    disabled={Boolean(readOnly)}
                    sx={{
                      ".MuiInput-input": {
                        color: "#0f172a",
                        fontWeight: "500",
                      },
                    }}
                  />
                  {isUserRequired && (
                    <FormHelperText error>
                      {t("Task.Validate.EnterAssignedTitle")}
                    </FormHelperText>
                  )}
                </Grid>                ) : (
                  <Grid item xs={6} sx={TASK_COL_RIGHT}>
                    <FormControl variant="standard" fullWidth>
                      <TextField
                        id="standard-basic"
                        label={t("Automation.Email")}
                        variant="standard"
                        onChange={(event) => {
                          this.setState({ 
                            selectedEmails: event.target.value, 
                            isTaskEmailRequired: false 
                          });
                        }}
                        placeholder={t("Task.Validate.SelectUsersLabel")}
                        value={selectedEmails}
                        disabled={Boolean(readOnly)}
                        error={isTaskEmailRequired}
                        helperText={
                          isTaskEmailRequired && t("Task.Validate.TaskEmail")
                        }
                      />
                    </FormControl>
                  </Grid>
                ) }
              </Grid>
              {selectedTaskTypeID !== TaskTypeEnum.MonitorLiveAgents ? (
              <Grid container sx={{ mt: "12px", width: "100%" }}>
                <Grid item xs={6} sx={TASK_COL_LEFT}>
                  <FormControl variant="standard" fullWidth>
                    <InputLabel id="demo-simple-select-standard-label">
                      {t("Task.StatusLabel")}
                    </InputLabel>
                    <Select
                      labelId="action-simple-select-standard-label"
                      id="action-simple-select-standard"
                      value={status}
                      onChange={(event) => {
                        this.setState({ status: event.target.value });
                      }}
                      label={t("Task.StatusLabel")}
                      sx={{
                        ".MuiInput-input": {
                          color: "#0f172a",
                          fontWeight: "500",
                        },
                      }}
                      disabled={Boolean(readOnly)}
                      renderValue={(value) => statusDisplay(value)}
                    >
                      {STATUS.map((items, index) => (
                        <MenuItem
                          key={index}
                          value={items}
                          sx={{
                            color: "#0f172a !important",
                            fontWeight: "400",
                          }}
                        >
                          {statusDisplay(items)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>                 
                </Grid> 
                <Grid item xs={6} sx={TASK_COL_RIGHT}>
                  <FormControl variant="standard" fullWidth>
                    <InputLabel id="demo-simple-select-standard-label">
                      {t("Task.PriorityLabel")}
                    </InputLabel>
                    <Select
                      labelId="action-simple-select-standard-label"
                      id="action-simple-select-standard"
                      value={priority}
                      onChange={(event) =>
                        this.setState({ priority: event.target.value })
                      }
                      label={t("Task.PriorityLabel")}
                      sx={{
                        ".MuiInput-input": {
                          color: "#0f172a",
                          fontWeight: "500",
                          ".Mui-disabled": {
                            color: "#0f172a",
                            fontWeight: "500",
                          },
                        },
                      }}
                      disabled={Boolean(readOnly)}
                    >
                      {PRIORITY.map((items, index) => (
                        <MenuItem
                          key={index}
                          value={items}
                          sx={{
                            color: "#0f172a !important",
                            fontWeight: "400",
                          }}
                        >
                          {items}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>) : (
                <Grid container sx={{ mt: "12px", width: "100%" }}>
                  <Grid item xs={6} sx={TASK_COL_LEFT}>
                    <FormControlLabel
                        label={
                          <span style={{ color: '#0f172a', fontSize: '1rem' }}>
                            {t('Patient.Enabled')}
                          </span>
                        }
                        labelPlacement='start'
                        control={
                            <Switch
                              name='checkConsult'
                              color='primary'
                              checked={this.state.isEanbled}
                              onChange={
                                this.handleSwitchChangeIsEnableTask
                              }
                              disabled={this.state.operation === 'View'}
                              value='checked'
                            />
                        }
                        style={{ marginLeft: '0px', marginTop: '5px' }}
                    />
                  </Grid>
                </Grid>
              )}
              {selectedTaskTypeID !== TaskTypeEnum.MonitorLiveAgents ? (
              <Grid container sx={{ mt: "15px" }}>
                <Grid item xs={12}>
                  {pickupChangeDetailsCard ? (
                    pickupChangeDetailsCard
                  ) : (
                    <FormControl fullWidth error={isTaskDetailsRequired}>
                      {openTaskManagerDialog ? (
                        <HtmlEditor
                          key={
                            (operation || "Add") +
                            "-" +
                            (this.state.selectedRowID || "new")
                          }
                          compact
                          enableImages={false}
                          height={160}
                          label={t("Task.TaskDetailsLabel")}
                          value={taskDetails || ""}
                          onChange={this.handleTaskDetailsChange}
                          disabled={Boolean(readOnly)}
                          error={isTaskDetailsRequired}
                        />
                      ) : null}
                      {isTaskDetailsRequired ? (
                        <FormHelperText>
                          {t("Task.Validate.EnterTaskDetails")}
                        </FormHelperText>
                      ) : null}
                    </FormControl>
                  )}
                </Grid>
              </Grid>) : null }
              {selectedTaskTypeID !== TaskTypeEnum.MonitorLiveAgents ? (
              <Grid container sx={{ mt: "12px", width: "100%" }}>
                <Grid item xs={6} sx={TASK_COL_LEFT}>
                  <SearchableSelect
                    label={t("Task.PatientLabel")}
                    items={patientDataList || []}
                    value={selectedPatientID ? String(selectedPatientID) : ""}
                    onChange={this.handleChangePatients}
                    placeholder="Search patients..."
                    id="task-patient-select"
                    fullWidth
                    variant="standard"
                    disabled={Boolean(readOnly)}
                    sx={{
                      ".MuiInput-input": {
                        color: "#0f172a",
                        fontWeight: "500",
                      },
                    }}
                  />
                  {isPatientRequired && (
                    <FormHelperText error>
                      {t("Task.Validate.EnterPatient")}
                    </FormHelperText>
                  )}
                </Grid>
                <Grid item xs={6} sx={TASK_COL_RIGHT}>
                  <TextField
                    id="standard-basic"
                    label={t("Task.OrderCode")}
                    variant="standard"
                    fullWidth
                    onChange={(event) => {
                      this.setState({ orderCode: event.target.value });
                    }}
                    value={orderCode}
                    disabled={Boolean(readOnly)}
                  />
                </Grid>
              </Grid>): null }
                </React.Fragment>
                  </Box>
                  <Box
                    sx={{
                      flex: "1 1 42%",
                      minWidth: 0,
                      p: "20px 24px",
                      overflowX: "hidden",
                      overflowY: "auto",
                      boxSizing: "border-box",
                      background:
                        "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
                      borderLeft: { md: "1px solid #e2e8f0" },
                      borderTop: { xs: "1px solid #e2e8f0", md: "none" },
                    }}
                  >
                    {this.renderTaskPreview()}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5, flexShrink: 0 }}>
              <Button
                onClick={this.handleCloseTaskManagerDialog}
                color="primary"
                className={classes["primaryButton"]}
              >
                {operation === "View" ? t("Task.Close") : t("Task.Cancel")}
              </Button>
              {operation !== "View" && (
                <Button
                  color="primary"
                  className={classes["primaryButton"]}
                  onClick={(event) => {
                    operation === "Add"
                      ? this.handleSubmit(event)
                      : this.handleUpdate(event);
                  }}
                >
                  {operation === "Add" ? t("Task.Save") : t("Task.Update")}
                </Button>
              )}
            </DialogActions>
          </Dialog>
          <Dialog
            maxWidth={"md"}
            fullWidth={true}
            open={openDeleteDialog || false}
            onClose={this.handleCloseDeleteDialog}
            aria-labelledby="form-dialog-title"
            PaperProps={{
              sx: { minWidth: "500px" },
            }}
          >
            {this.renderLoaderIndicator()}
            <BootstrapDialogTitle
              id="customized-dialog-title"
              onDialogClose={this.handleCloseDeleteDialog}
            >
              {t("Task.AreYouSureToDeleteText")}
            </BootstrapDialogTitle>
            <DialogContent dividers>
              <Box component="div">
                {confirmedDeleteList &&
                  confirmedDeleteList.map((ele, index) => (
                    <Box
                      component="div"
                      key={index}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        m: "0.5rem",
                      }}
                    >
                      {" • "} {ele["task_name"]}
                    </Box>
                  ))}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={this.handleCloseDeleteDialog}
                color="primary"
                className={classes["primaryButton"]}
              >
                {t("Task.Cancel")}
              </Button>
              <Button
                color="primary"
                className={classes["primaryButton"]}
                onClick={this.handleConfirmDelete}
              >
                {t("Task.Delete")}
              </Button>
            </DialogActions>
          </Dialog>
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <QueueLaneTiles
              summary={queueSummary}
              activeLane={activeLane}
              onSelect={this.handleLaneSelect}
              loading={queueSummaryLoading}
            />
          </Box>
          <EnhancedTableToolbar
            TotalRowsSelected={selectedData.length || 0}
            openTaskManagerDialogClick={this.handleOpenTaskManagerDialog}
            DeleteTaskClick={this.handleOpenDeleteDialog}
          />
        </Box>
        <Divider />
        <Box component="div" sx={{ overflowX: "auto" }}>
          <ILocalAGGrid
            columnDefs={this.getColumnDefs()}
            defaultColDef={getDefaultColDef({ minWidth: 100 })}
            rowSelection="multiple"
            rowModelType="serverSide"
            cacheBlockSize={filterObj.pageSize || 10}
            paginationPageSize={filterObj.pageSize || 10}
            paginationPageSizeSelector={[10, 20, 25]}
            rowHeight={50}
            getRowId={(params) => params.data && String(params.data.id)}
            onGridReady={this.onGridReady}
            onRowSelected={this.onRowSelected}
            onRowClicked={this.handleRowClicked}
            onPaginationChanged={this.onPaginationChanged}
            getContextMenuItems={this.getContextMenuItems}
          />
        </Box>
      </Box>
    );
  }
}

export default withStyles(useStyles, { withTheme: true })(
  withTranslation()(TaskManager)
);



// WEBPACK FOOTER //
// ./src/components/taskmanager/index.js